/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { Grid } from '@mui/material';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import FormInput from './FormInput';

export interface AddressFields {
    numeroVoie: string;
    lettreVoie: string;
    typeVoie: string;
    libelleVoie: string;
    lieuDit: string;
    complement: string;
    codePostal: string;
    ville: string;
}

export type AddressErrors = Partial<Record<keyof AddressFields, string>>;

interface AddressDetailedProps {
    value: AddressFields;
    onChange: (fields: AddressFields) => void;
    errors?: AddressErrors;
}

export const emptyAddressFields: AddressFields = {
    numeroVoie: '',
    lettreVoie: '',
    typeVoie: '',
    libelleVoie: '',
    lieuDit: '',
    complement: '',
    codePostal: '',
    ville: '',
};

const ROUTE_TYPES = [
    'Grande Rue', 'Boulevard', 'Résidence', 'Esplanade', 'Lotissement',
    'Traverse', 'Sentier', 'Passage', 'Allée', 'Avenue', 'Chemin',
    'Domaine', 'Impasse', 'Square', 'Place', 'Route', 'Quai',
    'Villa', 'Voie', 'Parc', 'Cité', 'Rue',
];

const parseRoute = (route: string): { typeVoie: string; libelleVoie: string } => {
    for (const type of ROUTE_TYPES) {
        const prefix = type.toLowerCase() + ' ';
        if (route.toLowerCase().startsWith(prefix)) {
            return { typeVoie: type, libelleVoie: route.slice(prefix.length) };
        }
    }
    return { typeVoie: '', libelleVoie: route };
};

const AddressDetailed: React.FC<AddressDetailedProps> = ({ value, onChange, errors }) => {
    const { brandConfig } = useTailwindBrand();
    const [searchOption, setSearchOption] = useState<any>(null);

    const handlePlaceSelect = useCallback(async (option: any) => {
        setSearchOption(option);
        if (!option?.value?.place_id) return;
        try {
            const results = await geocodeByPlaceId(option.value.place_id);
            if (!results?.[0]) return;
            const comps = results[0].address_components;
            const get = (...types: string[]) =>
                comps.find((c: any) => types.some(t => c.types.includes(t)))?.long_name ?? '';

            const route = get('route');
            const { typeVoie, libelleVoie } = parseRoute(route);

            onChange({
                ...value,
                numeroVoie: get('street_number'),
                typeVoie,
                libelleVoie,
                lieuDit: get('sublocality', 'sublocality_level_1'),
                codePostal: get('postal_code'),
                ville: get('locality'),
            });
        } catch {
            /* l'utilisateur peut remplir manuellement */
        }
    }, [value, onChange]);

    const handleField = (field: keyof AddressFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ...value, [field]: e.target.value });
    };

    return (
        <div className="flex flex-col gap-y-4">
            <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white hover:border-gray-400 transition-colors">
                <p className="text-xs text-gray-500 mb-1 font-medium">
                    Rechercher une adresse (l'auto-complétion remplira les champs ci-dessous)
                </p>
                <GooglePlacesAutocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                    apiOptions={{ language: 'fr', region: 'fr' }}
                    autocompletionRequest={{
                        componentRestrictions: { country: 'fr' },
                        types: ['address'],
                    }}
                    selectProps={{
                        value: searchOption,
                        onChange: handlePlaceSelect,
                        isClearable: true,
                        placeholder: 'Ex : 22 rue de la Paix, Paris',
                        noOptionsMessage: () => 'Aucune adresse trouvée',
                        loadingMessage: () => 'Recherche en cours...',
                        styles: {
                            input: (p: any) => ({ ...p, padding: '0', fontFamily: brandConfig.fontFamily }),
                            placeholder: (p: any) => ({ ...p, fontFamily: brandConfig.fontFamily, color: '#9ca3af' }),
                            dropdownIndicator: () => ({ display: 'none' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            control: (p: any) => ({
                                ...p,
                                border: 0,
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                                minHeight: 0,
                                fontFamily: brandConfig.fontFamily,
                            }),
                            menu: (p: any) => ({ ...p, fontFamily: brandConfig.fontFamily, zIndex: 9999 }),
                            option: (p: any, state: any) => ({
                                ...p,
                                fontFamily: brandConfig.fontFamily,
                                backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
                                color: '#111827',
                                cursor: 'pointer',
                            }),
                        },
                    }}
                />
            </div>

            <Grid container spacing={2}>
                <Grid item xs={4} md={2}>
                    <FormInput
                        label="N° de voie"
                        type="text"
                        placeholder="Ex : 22"
                        value={value.numeroVoie}
                        onChange={handleField('numeroVoie')}
                        error={errors?.numeroVoie}
                    />
                </Grid>
                {/* <Grid item xs={4} md={2}>
                    <FormInput
                        label="Lettre de voie"
                        type="text"
                        placeholder="Ex : Bis"
                        value={value.lettreVoie}
                        onChange={handleField('lettreVoie')}
                        error={errors?.lettreVoie}
                    />
                </Grid> */}
                <Grid item xs={4} md={3}>
                    <FormInput
                        label="Type de voie"
                        type="text"
                        placeholder="Ex : Rue"
                        value={value.typeVoie}
                        onChange={handleField('typeVoie')}
                        error={errors?.typeVoie}
                    />
                </Grid>
                <Grid item xs={12} md={7}>
                    <FormInput
                        label="Libellé de voie"
                        type="text"
                        placeholder="Ex : de la Paix"
                        value={value.libelleVoie}
                        onChange={handleField('libelleVoie')}
                        error={errors?.libelleVoie}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="Lieu-dit"
                        type="text"
                        placeholder="Facultatif"
                        value={value.lieuDit}
                        onChange={handleField('lieuDit')}
                        error={errors?.lieuDit}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="Complément d'adresse"
                        type="text"
                        placeholder="Bât, appt, étage... (facultatif)"
                        value={value.complement}
                        onChange={handleField('complement')}
                        error={errors?.complement}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormInput
                        label="Code postal"
                        type="text"
                        placeholder="Ex : 75001"
                        value={value.codePostal}
                        onChange={handleField('codePostal')}
                        error={errors?.codePostal}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <FormInput
                        label="Ville"
                        type="text"
                        placeholder="Ex : Paris"
                        value={value.ville}
                        onChange={handleField('ville')}
                        error={errors?.ville}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default AddressDetailed;
